/**
 * New Bill Page - Point of Sale
 *
 * Mobile-first POS interface with full Hacienda v4.4 support.
 * Features: Client selection, document types, payment methods, line editing
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Search,
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  Banknote,
  CreditCard,
  ArrowLeft,
  Smartphone,
  Loader2,
  User,
  ChevronDown,
  ChevronUp,
  FileText,
  Pencil,
  X,
  Save,
  Receipt,
  AlertTriangle,
  Check,
  ScanBarcode,
  MoreHorizontal,
  MoreVertical,
} from 'lucide-react'

import api from '@/lib/api'
import { isAxiosError } from 'axios'
import { formatCurrency, debounce } from '@/lib/utils'
import { t } from '@/i18n'
import { useAuth } from '@/hooks/useAuth'
import { OPEN_APPROVAL_STATUSES, useApprovalRequests } from '@/hooks/useApprovals'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useToast } from '@/components/ui/toaster'
import { ClientCreditAlert, type ClientAccountSummary } from '@/components/clients/ClientCreditAlert'
import { cn } from '@/lib/utils'
import { PaymentModal, PaymentMethod } from '@/components/billing/PaymentModal'
import { ActivityCodeSelect } from '@/components/clients/ActivityCodeSelect'
import type { PaymentData } from '@/components/billing/PaymentModal'
import { EXONERATION_INSTITUTION_OPTIONS } from '@/types/client'
import {
  playScanErrorSound,
  playScanReadSound,
  primeScanAudio,
  useBarcodeScanner,
} from '@/lib/barcodeScanner'
import { shouldOfferCameraScan } from '@/lib/cameraScanner'
import { CameraScannerDialog } from '@/components/billing/CameraScannerDialog'

// Types
interface Client {
  id: number
  name: string
  identification: string
  identification_type: string
  email: string
  phone: string
  price_list: number
  term_days: number
  credit_limit: number
  current_balance: number
  payment_days: number
  credit_blocked?: boolean
  activity_code?: string
  activity_code_name?: string
  is_exonerated?: boolean
  exoneration_type?: string
  exoneration_number?: string
  exoneration_institution?: string
  exoneration_institution_code?: string
  exoneration_percentage?: number
}

interface BillLine {
  id?: number
  article_id: number
  article_code: string
  article_description: string
  quantity: number
  unit_price: number
  discount_percent: number
  subtotal: number
  tax_amount: number
  total: number
  cabys_code?: string
  ubication_name?: string
  article_stock?: number | null
  article_minimum_stock?: number | null
}

interface BillTotals {
  subtotal: number
  subtotal_2: number
  total_general_discount: number
  total_line_discount: number
  total_sale_tax: number
  total: number
}

interface Article {
  id: number
  code: string
  description: string
  sale_price_1: number
  sale_price_2: number
  sale_price_3: number
  sale_price_1_iva: number
  sale_price_2_iva: number
  sale_price_3_iva: number
  count_articles: number
  cabys_code: string
  applied_sale_tax: boolean
  alternative_code?: string
  ubication_name?: string
}

/** `onDone` recibe si el artículo quedó en la factura. */
type ScanJob = ({ code: string } | { article: Article }) & { onDone?: (added: boolean) => void }

// Document types for Hacienda
const DOCUMENT_TYPES = [
  { value: 'ticket', label: t('pos.ticketElectronic') },
  { value: 'invoice', label: t('pos.invoiceElectronic') },
  { value: 'proforma', label: t('pos.proforma') },
]

// Payment methods used in POS. Hacienda v4.4 defines 01-07 and 99;
// we omit 05 (recaudado por terceros) and 07 (plataformas digitales) because they
// do not apply to the day-to-day POS flow.
const PAYMENT_METHODS_HACIENDA = [
  { value: '01', label: t('pos.paymentCash'), icon: 'cash' },
  { value: '02', label: t('pos.paymentCard'), icon: 'card' },
  { value: '03', label: t('pos.paymentCheck'), icon: 'check' },
  { value: '04', label: t('pos.paymentTransfer'), icon: 'transfer' },
  { value: '06', label: t('pos.paymentSinpe'), icon: 'sinpe' },
]

// Línea tal como la devuelve GET /bills/{id}, con los nombres viejos de respaldo.
type ApiBillLine = {
  id: number
  article_id: number
  article_code?: string
  article_description?: string
  code?: string
  description?: string
  quantity: number
  unit_price: number
  discount_percent?: number
  subtotal: number
  tax_amount?: number
  total: number
  cabys_code?: string
  article_stock?: number | null
  article_minimum_stock?: number | null
  ubication_name?: string | null
}

const mapBillLine = (line: ApiBillLine): BillLine => ({
  id: line.id,
  article_id: line.article_id,
  article_code: line.article_code || line.code || '',
  article_description: line.article_description || line.description || '',
  quantity: line.quantity,
  unit_price: line.unit_price,
  discount_percent: line.discount_percent || 0,
  subtotal: line.subtotal,
  tax_amount: line.tax_amount || 0,
  total: line.total,
  cabys_code: line.cabys_code,
  article_stock: line.article_stock ?? null,
  article_minimum_stock: line.article_minimum_stock ?? null,
  ubication_name: line.ubication_name ?? undefined,
})

// Convert Hacienda payment method code to PaymentMethod enum
const getPaymentMethodFromCode = (code: string): PaymentMethod => {
  switch (code) {
    case '01': return PaymentMethod.CASH
    case '02': return PaymentMethod.CARD
    case '03': return PaymentMethod.CHECK
    case '04': return PaymentMethod.TRANSFER
    case '06': return PaymentMethod.SINPE
    case '99': return PaymentMethod.OTHER
    default: return PaymentMethod.CASH
  }
}

// ID types for Costa Rica (Alanube v44: 01-05)
const ID_TYPES = [
  { value: '01', label: t('idTypes.type01') },
  { value: '02', label: t('idTypes.type02') },
  { value: '03', label: t('idTypes.type03') },
  { value: '04', label: t('idTypes.type04') },
  { value: '05', label: t('idTypes.type05') },
]

// Exoneration types for v4.4 (Alanube supported)
const EXONERATION_TYPES = [
  { value: '01', label: t('pos.exonerationTypes.t01') },
  { value: '02', label: t('pos.exonerationTypes.t02') },
  { value: '03', label: t('pos.exonerationTypes.t03') },
  { value: '04', label: t('pos.exonerationTypes.t04') },
  { value: '05', label: t('pos.exonerationTypes.t05') },
  { value: '06', label: t('pos.exonerationTypes.t06') },
  { value: '07', label: t('pos.exonerationTypes.t07') },
  { value: '99', label: t('pos.exonerationTypes.t99') },
]

export function NewBillPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Check if continuing an existing bill
  const continueBillId = searchParams.get('continue')
  const initialDocumentType = (searchParams.get('type') as 'ticket' | 'invoice' | 'proforma' | null) || 'ticket'

  // ============ CASHBOX CHECK ============
  const { data: cashbox, isLoading: isLoadingCashbox } = useQuery({
    queryKey: ['my-cashbox'],
    queryFn: async () => {
      const response = await api.get('/cashboxes/my-cashbox')
      return response.data
    },
  })

  const hasCashboxOpen = cashbox?.is_open === true

  // ============ TENANT DATA (for activity codes) ============
  const { data: tenantData } = useQuery({
    queryKey: ['tenant-current'],
    queryFn: async () => {
      const response = await api.get('/tenants/current')
      return response.data
    },
  })
  const tenantActivities: Array<{ code: string; name: string; tax_rate: string }> = (tenantData?.activity_codes || []).map((a: any) =>
    typeof a === 'string' ? { code: a, name: a, tax_rate: '01' } : a
  )

  // ============ BILL STATE ============
  const [billId, setBillId] = useState<number | null>(null)
  // El vendedor vende solo a precio 1; los descuentos los aprueba un admin.
  const { isSeller } = useAuth()
  const { data: openDiscounts = [] } = useApprovalRequests(
    { billId: billId ?? undefined, kind: 'discount', status: OPEN_APPROVAL_STATUSES },
    { enabled: isSeller && !!billId, poll: true }
  )
  const [lines, setLines] = useState<BillLine[]>([])
  // Los escaneos se procesan en cola, fuera del ciclo de render: leen de acá
  // para no usar la factura y las líneas de un render viejo.
  const billIdRef = useRef(billId)
  billIdRef.current = billId
  const linesRef = useRef(lines)
  linesRef.current = lines
  const [totals, setTotals] = useState<BillTotals>({
    subtotal: 0,
    subtotal_2: 0,
    total_general_discount: 0,
    total_line_discount: 0,
    total_sale_tax: 0,
    total: 0,
  })

  // ============ DOCUMENT CONFIG ============
  const [documentType, setDocumentType] = useState<string>(initialDocumentType)
  // Validez por defecto de proformas: 30 días desde hoy.
  const [expirationDate, setExpirationDate] = useState<string>(() => {
    const d = new Date()
    d.setDate(d.getDate() + 30)
    return d.toISOString().slice(0, 10)
  })
  const [paymentMethod, setPaymentMethod] = useState('') // Empty = force user to select
  const [paymentType, setPaymentType] = useState<'cash' | 'credit'>('cash')
  const [termDays, setTermDays] = useState(0)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  // Línea pendiente de confirmar para quitar: el botón queda junto al total y es fácil tocarlo sin querer
  const [lineToRemove, setLineToRemove] = useState<BillLine | null>(null)
  const [confirmCancelBill, setConfirmCancelBill] = useState(false)
  const [creditBlockedNotice, setCreditBlockedNotice] = useState(false)

  // ============ ACTIVITY CODE STATE ============
  const [activityCode, setActivityCode] = useState('')
  const [receiverActivityCode, setReceiverActivityCode] = useState('')
  const [receiverActivityName, setReceiverActivityName] = useState('')
  const [clientInfoSaved, setClientInfoSaved] = useState(false)

  // ============ EXONERATION STATE (v4.4) ============
  const [isExonerated, setIsExonerated] = useState(false)
  const [exonerationType, setExonerationType] = useState('')
  const [exonerationNumber, setExonerationNumber] = useState('')
  const [exonerationInstitution, setExonerationInstitution] = useState('')
  const [exonerationInstitutionCode, setExonerationInstitutionCode] = useState('')
  const [exonerationPercentage, setExonerationPercentage] = useState(100)

  // ============ CLIENT STATE ============
  const [showClientSection, setShowClientSection] = useState(false)
  const [clientSearchQuery, setClientSearchQuery] = useState('')
  const [, setClientSearchInput] = useState('')
  const [showClientResults, setShowClientResults] = useState(false)
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const restoringClientRef = useRef(false)

  // Cliente con crédito bloqueado: la factura vuelve a contado sin importar si
  // el crédito se eligió antes o después del cliente, o venía de un borrador.
  useEffect(() => {
    if (selectedClient?.credit_blocked && paymentType === 'credit') {
      setPaymentType('cash')
      setTermDays(0)
      setCreditBlockedNotice(true)
    }
  }, [selectedClient, paymentType])

  // Resumen de cuenta del cliente: sirve para avisarle al cajero, en el momento
  // de abrir la venta, si el cliente se pasó del límite de crédito o arrastra
  // facturas vencidas. Es solo un aviso, no bloquea nada.
  const { data: clientAccount } = useQuery<ClientAccountSummary>({
    queryKey: ['client-account-summary', selectedClient?.id],
    queryFn: async () => {
      const response = await api.get(`/clients/${selectedClient!.id}/account-summary`)
      return response.data
    },
    enabled: !!selectedClient?.id,
    staleTime: 30_000,
  })
  const [clientName, setClientName] = useState('')
  const [clientNote, setClientNote] = useState('')
  const [clientIdType, setClientIdType] = useState('01')
  const [clientId, setClientId] = useState('')
  const [clientEmail, setClientEmail] = useState('')
  const clientSearchRef = useRef<HTMLDivElement>(null)

  // ============ ARTICLE STATE ============
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [showResults, setShowResults] = useState(false)
  const [selectedPriceTier, setSelectedPriceTier] = useState<1 | 2 | 3>(1)
  const searchContainerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const scanQueueRef = useRef<ScanJob[]>([])
  const scanBusyRef = useRef(false)
  const [pendingScans, setPendingScans] = useState(0)
  const [scanCandidates, setScanCandidates] = useState<{ code: string; items: Article[] } | null>(null)
  const [highlightedLineId, setHighlightedLineId] = useState<number | null>(null)
  const [showCamera, setShowCamera] = useState(false)
  // Confirmación del último escaneo, para no tener que buscar la línea en la lista.
  const [lastScan, setLastScan] = useState<{ ok: boolean; text: string } | null>(null)
  const [typing, setTyping] = useState(false)
  const [cameraAvailable] = useState(shouldOfferCameraScan)

  // ============ UI STATE ============
  const [pendingArticle, setPendingArticle] = useState<{
    code: string
    description: string
    price: number
    stock: number
  } | null>(null)

  // ============ LOAD EXISTING BILL ============

  // Load existing bill if continuing
  const { data: existingBill } = useQuery({
    queryKey: ['bill', continueBillId],
    queryFn: async () => {
      const response = await api.get(`/bills/${continueBillId}`)
      return response.data
    },
    enabled: !!continueBillId,
    // Siempre fresco: con la caché global de 5 min, reabrir una factura recién
    // guardada mostraba los datos de antes de guardarla.
    staleTime: 0,
    gcTime: 0,
  })

  // Populate state from existing bill
  useEffect(() => {
    if (existingBill && continueBillId) {
      setBillId(existingBill.id)
      setDocumentType(existingBill.bill_type || 'ticket')
      if (existingBill.expiration_date) {
        setExpirationDate(String(existingBill.expiration_date).slice(0, 10))
      }
      setPaymentMethod(existingBill.payment_method_code || '')
      setPaymentType(existingBill.payment_type === 'credit' ? 'credit' : 'cash')
      setTermDays(existingBill.term_days || 0)

      // Client data
      if (existingBill.client_name) {
        setClientName(existingBill.client_name)
      }
      if (existingBill.client_identification) {
        setClientId(existingBill.client_identification)
        setClientIdType(existingBill.client_id_type || '01')
      }
      if (existingBill.client_email) {
        setClientEmail(existingBill.client_email)
      }
      if (existingBill.client_id) {
        api.get(`/clients/${existingBill.client_id}`).then((response) => {
          restoringClientRef.current = true
          setSelectedClient(response.data)
        }).catch(() => { /* queda con los datos copiados en la factura */ })
      }
      if (existingBill.notes) {
        setClientNote(existingBill.notes)
      }

      // Activity codes
      if (existingBill.activity_code) {
        setActivityCode(existingBill.activity_code)
      }
      if (existingBill.receiver_activity_code) {
        setReceiverActivityCode(existingBill.receiver_activity_code)
      }

      // Exoneration
      if (existingBill.is_exonerated) {
        setIsExonerated(true)
        setExonerationType(existingBill.exoneration_type || '')
        setExonerationNumber(existingBill.exoneration_number || '')
        setExonerationInstitution(existingBill.exoneration_institution || '')
        setExonerationInstitutionCode(existingBill.exoneration_institution_code || '')
        setExonerationPercentage(existingBill.exoneration_percentage || 100)
      }

      // Lines
      if (existingBill.lines && existingBill.lines.length > 0) {
        const mappedLines: BillLine[] = existingBill.lines.map(mapBillLine)
        setLines(mappedLines)
      }

      // Totals
      if (existingBill.total !== undefined) {
        setTotals({
          subtotal: existingBill.subtotal || 0,
          subtotal_2: existingBill.subtotal_2 || 0,
          total_general_discount: existingBill.total_general_discount || 0,
          total_line_discount: existingBill.total_line_discount || 0,
          total_sale_tax: existingBill.total_sale_tax || 0,
          total: existingBill.total || 0,
        })
      }
    }
  }, [existingBill, continueBillId])

  // ============ EFFECTS ============

  // Auto-select activity code when tenant has exactly one activity
  useEffect(() => {
    if (tenantActivities.length === 1 && !activityCode) {
      setActivityCode(tenantActivities[0].code)
    }
  }, [tenantActivities, activityCode])

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowResults(false)
      }
      if (clientSearchRef.current && !clientSearchRef.current.contains(event.target as Node)) {
        setShowClientResults(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [])

  // Vacío no son 30 días: vence según el plazo de la ficha del cliente, y si
  // no tiene, nunca (ver aviso de crédito). El "30" fijo hacía creer otra cosa.
  const creditDaysPlaceholder = selectedClient?.payment_days
    ? String(selectedClient.payment_days)
    : t('pos.noCreditTerm')

  // Update client fields when client is selected
  useEffect(() => {
    if (selectedClient) {
      // Al reabrir, plazo y actividad del receptor son los guardados en la factura.
      if (restoringClientRef.current) {
        restoringClientRef.current = false
        return
      }
      setClientName(selectedClient.name)
      setClientIdType(selectedClient.identification_type || '01')
      setClientId(selectedClient.identification || '')
      setClientEmail(selectedClient.email || '')
      setTermDays(selectedClient.term_days || 0)
      setSelectedPriceTier(isSeller ? 1 : (selectedClient.price_list as 1 | 2 | 3) || 1)
      // La actividad del receptor NUNCA se precarga: es de cada factura y el
      // usuario la elige (o usa el botón "usar la del cliente").
      setReceiverActivityCode('')
      setReceiverActivityName('')
    }
  }, [selectedClient])

  // ============ AUTO-SAVE HELPERS ============

  // Solo auto-guarda el email. La actividad elegida en el POS aplica SOLO a la
  // factura: la ficha del cliente se modifica únicamente desde su formulario.
  const autoSaveClientInfo = useCallback(async () => {
    if (!selectedClient) return
    const changes: Record<string, string | undefined> = {}
    if (clientEmail !== (selectedClient.email || '')) {
      changes.email = clientEmail || undefined
    }
    if (Object.keys(changes).length === 0) return
    try {
      await api.put(`/clients/${selectedClient.id}`, changes)
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['client-search'] })
      setClientInfoSaved(true)
      setTimeout(() => setClientInfoSaved(false), 2000)
    } catch { /* non-blocking */ }
  }, [selectedClient, clientEmail, queryClient])

  // ============ MUTATIONS ============

  // Create bill
  const createBillMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/bills/', {
        client_id: selectedClient?.id || null,
        client_name: clientName || null,
        notes: clientNote.trim() || null,
        bill_type: documentType,
        pay_type: paymentType,
        payment_method_code: paymentMethod,
        general_discount: 0,
        term_days: paymentType === 'credit' ? termDays : 0,
        // Activity codes
        activity_code: activityCode || null,
        receiver_activity_code: receiverActivityCode || null,
        // Validez (sólo proformas — el backend ignora el campo en otros tipos)
        expiration_date: documentType === 'proforma' ? expirationDate : null,
        // Exoneration fields (v4.4)
        is_exonerated: isExonerated,
        exoneration_type: isExonerated ? exonerationType : null,
        exoneration_number: isExonerated ? exonerationNumber : null,
        exoneration_institution: isExonerated ? exonerationInstitution : null,
        exoneration_institution_code: isExonerated ? exonerationInstitutionCode : null,
        exoneration_percentage: isExonerated ? exonerationPercentage : null,
      })
      return response.data
    },
    onSuccess: (data) => {
      billIdRef.current = data.id
      setBillId(data.id)
    },
  })

  // Add line
  const addLineMutation = useMutation({
    mutationFn: async (data: {
      article_id: number
      quantity: number
      unit_price: number
      discount_percent: number
      cabys_code?: string
      ubication_name?: string
    }) => {
      let currentBillId = billIdRef.current
      if (!currentBillId) {
        const bill = await createBillMutation.mutateAsync()
        currentBillId = bill.id
      }
      const { ubication_name, ...apiData } = data
      const response = await api.post(`/bills/${currentBillId}/lines`, apiData)
      return { ...response.data, ubication_name }
    },
    onSuccess: (lineData) => {
      setLines((prev) => [...prev, lineData])
      refreshTotals()
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      const message = error.response?.data?.detail || t('errors.addArticleError')
      toast({
        title: t('common.error'),
        description: message.includes('Insufficient stock')
          ? t('pos.noStockAvailable')
          : message,
        variant: 'destructive',
      })
    },
  })

  // Update line
  const updateLineMutation = useMutation({
    mutationFn: async (data: {
      lineId: number
      quantity: number
      unit_price: number
      discount_percent: number
    }) => {
      const response = await api.put(`/bills/${billIdRef.current}/lines/${data.lineId}`, {
        quantity: data.quantity,
        unit_price: data.unit_price,
        discount_percent: data.discount_percent,
      })
      return response.data
    },
    onSuccess: (responseData) => {
      // Update line in local state with data from response
      setLines((prev) =>
        prev.map((l) =>
          l.id === responseData.id
            ? {
                ...l,
                id: responseData.id,
                article_id: responseData.article_id,
                article_code: responseData.article_code,
                article_description: responseData.article_description,
                quantity: responseData.quantity,
                unit_price: responseData.unit_price,
                discount_percent: responseData.discount_percent,
                subtotal: responseData.subtotal,
                total: responseData.total,
                cabys_code: responseData.cabys_code,
                article_stock: responseData.article_stock,
                article_minimum_stock: responseData.article_minimum_stock ?? l.article_minimum_stock,
                ubication_name: responseData.ubication_name ?? l.ubication_name,
              }
            : l
        )
      )
      // Update totals from response (no need for extra API call)
      setTotals({
        subtotal: responseData.bill_subtotal,
        subtotal_2: responseData.bill_subtotal_2,
        total_general_discount: responseData.bill_total_general_discount,
        total_line_discount: responseData.bill_total_line_discount,
        total_sale_tax: responseData.bill_total_sale_tax,
        total: responseData.bill_total,
      })
    },
    onError: () => {
      toast({
        title: t('common.error'),
        description: t('errors.updateLineError'),
        variant: 'destructive',
      })
    },
  })

  // Remove line
  const removeLineMutation = useMutation({
    mutationFn: async (lineId: number) => {
      await api.delete(`/bills/${billId}/lines/${lineId}`)
      return lineId
    },
    onSuccess: (lineId) => {
      setLines((prev) => prev.filter((l) => l.id !== lineId))
      refreshTotals()
    },
  })

  // La factura no guarda la cédula, solo el cliente ligado: si se escribió a
  // mano sin elegir un cliente, se busca por cédula o se crea para no perderla.
  const linkTypedClient = async (): Promise<number | null> => {
    if (selectedClient) return selectedClient.id
    if (!clientId || !clientName) return null
    try {
      const searchResp = await api.get(`/clients/?search=${clientId}&page_size=1`)
      const matches = searchResp.data.items || []
      const exactMatch = matches.find((c: Client) => c.identification === clientId)
      let linkedClientId: number
      if (exactMatch) {
        linkedClientId = exactMatch.id
        await api.put(`/clients/${exactMatch.id}`, {
          name: clientName,
          email: clientEmail || undefined,
        })
      } else {
        const createResp = await api.post('/clients/', {
          name: clientName,
          type_identification: clientIdType,
          identification: clientId,
          email: clientEmail || undefined,
        })
        linkedClientId = createResp.data.id
      }
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      return linkedClientId
    } catch {
      return null
    }
  }

  const persistDraft = async () => {
    if (!billId) return
    await api.put(`/bills/${billId}`, {
      client_id: await linkTypedClient(),
      client_name: clientName,
      notes: clientNote.trim() || null,
      bill_type: documentType,
      pay_type: paymentType,
      payment_method_code: paymentMethod,
      term_days: paymentType === 'credit' ? termDays : 0,
      expiration_date: documentType === 'proforma' ? expirationDate : null,
      // Activity codes: deben persistirse en el borrador, si no al reabrirlo
      // (continuar luego) la actividad economica aparece deseleccionada y la
      // validacion vuelve a exigirla.
      activity_code: activityCode || null,
      receiver_activity_code: receiverActivityCode || null,
      // Exoneration fields (v4.4)
      is_exonerated: isExonerated,
      exoneration_type: isExonerated ? exonerationType : null,
      exoneration_number: isExonerated ? exonerationNumber : null,
      exoneration_institution: isExonerated ? exonerationInstitution : null,
      exoneration_institution_code: isExonerated ? exonerationInstitutionCode : null,
      exoneration_percentage: isExonerated ? exonerationPercentage : null,
    })
  }

  // Corregir el CABYS o editar una línea sale del POS: sin guardar antes, al
  // volver se perdía lo escrito en el cliente y la actividad.
  const leavePosTo = async (path: string) => {
    try {
      await persistDraft()
    } catch { /* se navega igual */ }
    navigate(path)
  }

  // Finalize bill
  const finalizeMutation = useMutation({
    mutationFn: async (data: { payment_type: string; amount_paid: number; payment_method_code?: string }) => {
      const linkedClientId = await linkTypedClient()

      // Update bill with latest client info and activity codes before finalizing.
      // bill_type/pay_type MUST be re-sent: the document type is captured at
      // bill creation (first line added, defaults to 'ticket'). If the user
      // switches to Factura Electronica / credito afterwards, that change only
      // lives in frontend state — without this the bill finalizes as Tiquete.
      await api.put(`/bills/${billId}`, {
        client_id: linkedClientId,
        client_name: clientName || undefined,
        notes: clientNote.trim() || null,
        bill_type: documentType,
        pay_type: paymentType,
        term_days: paymentType === 'credit' ? termDays : 0,
        activity_code: activityCode || undefined,
        receiver_activity_code: receiverActivityCode || null,
      })

      // Auto-save changed client info back to client profile
      if (selectedClient) {
        const clientUpdates: Record<string, string | undefined> = {}
        if (clientEmail !== (selectedClient.email || '')) {
          clientUpdates.email = clientEmail || undefined
        }
        if (Object.keys(clientUpdates).length > 0) {
          try {
            await api.put(`/clients/${selectedClient.id}`, clientUpdates)
            queryClient.invalidateQueries({ queryKey: ['clients'] })
          } catch { /* non-blocking */ }
        }
      }
      const response = await api.post(`/bills/${billId}/finalize`, {
        ...data,
        currency: 'CRC',
        term_days: paymentType === 'credit' ? termDays : 0,
        payment_method_code: data.payment_method_code || paymentMethod || (paymentType === 'credit' ? '99' : ''),
      })
      return response.data
    },
    onSuccess: async (data) => {
      setShowPaymentModal(false)
      // Invalidate and wait for bills query to refresh before navigating
      await queryClient.invalidateQueries({ queryKey: ['bills'] })

      toast({
        title: t('pos.billCompleted'),
        description: `${documentType === 'invoice' ? t('billing.invoice') : t('billing.ticket')} ${t('pos.billSavedOk')}`,
        variant: 'success',
      })
      // Navigate to the newly created bill
      navigate(`/bills/${data.id || billId}`)
    },
    onError: () => {
      toast({
        title: t('common.error'),
        description: t('errors.finalizeError'),
        variant: 'destructive',
      })
    },
  })

  // Save draft
  const saveDraftMutation = useMutation({
    mutationFn: persistDraft,
    onSuccess: async () => {
      const isProforma = documentType === 'proforma'
      // /bills usa el cache global de 5 min, asi que sin invalidar el draft
      // recien guardado no aparece. /proformas no requiere invalidacion
      // porque ProformasPage usa staleTime: 0 + refetchOnMount: 'always'.
      await queryClient.invalidateQueries({ queryKey: ['bills'] })
      toast({
        title: isProforma ? t('proformas.saved') : t('pos.saved'),
        description: isProforma ? t('proformas.savedDescription') : t('pos.savedAsDraft'),
        variant: 'success',
      })
      navigate(isProforma && billId ? `/proformas/${billId}` : '/bills')
    },
  })

  // Cancel bill
  const cancelBillMutation = useMutation({
    mutationFn: async () => {
      if (!billId) return
      await api.delete(`/bills/${billId}`)
    },
    onSuccess: () => {
      // Sin esto la lista muestra la factura con el estado de antes hasta recargar.
      queryClient.invalidateQueries({ queryKey: ['bills'] })
      queryClient.invalidateQueries({ queryKey: ['proformas'] })
      queryClient.invalidateQueries({ queryKey: ['articles'] })
      toast({
        title: t('pos.cancelled'),
        description: t('pos.billCancelled'),
      })
      navigate('/bills')
    },
  })

  // ============ QUERIES ============

  // Search articles
  const { data: searchResults, isFetching: isSearching } = useQuery({
    queryKey: ['article-search', searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return []
      const response = await api.get('/articles/', { params: { search: searchQuery, page_size: 20 } })
      return response.data.items as Article[]
    },
    enabled: searchQuery.length >= 2,
  })

  // Search clients
  const { data: clientResults, isFetching: isSearchingClients } = useQuery({
    queryKey: ['client-search', clientSearchQuery],
    queryFn: async () => {
      if (!clientSearchQuery || clientSearchQuery.length < 2) return []
      const response = await api.get(`/clients/?search=${clientSearchQuery}&page_size=10`)
      return response.data.items as Client[]
    },
    enabled: clientSearchQuery.length >= 2,
  })

  // ============ HANDLERS ============

  const refreshTotals = useCallback(async () => {
    const currentBillId = billIdRef.current
    if (!currentBillId) return
    try {
      const response = await api.get(`/bills/${currentBillId}/totals`)
      setTotals(response.data)
    } catch {
      // Ignore errors
    }
  }, [])

  // Cuando el admin aprueba un descuento, la solicitud sale de las abiertas. Se
  // actualizan solo las líneas que ya están en pantalla (descuento y montos), sin
  // reemplazar la lista: un escaneo en curso agrega líneas y no se puede perder.
  const openDiscountCount = useRef(0)
  useEffect(() => {
    const previous = openDiscountCount.current
    openDiscountCount.current = openDiscounts.length
    const requestedBillId = billIdRef.current
    if (openDiscounts.length >= previous || !requestedBillId) return
    api
      .get(`/bills/${requestedBillId}`)
      .then((response) => {
        if (billIdRef.current !== requestedBillId) return
        const fresh = new Map<number, BillLine>(
          (response.data.lines ?? []).map((line: ApiBillLine) => [line.id, mapBillLine(line)])
        )
        setLines((current) =>
          current.map((line) => {
            const updated = line.id != null ? fresh.get(line.id) : undefined
            return updated && updated.quantity === line.quantity
              ? {
                  ...line,
                  discount_percent: updated.discount_percent,
                  subtotal: updated.subtotal,
                  tax_amount: updated.tax_amount,
                  total: updated.total,
                }
              : line
          })
        )
        refreshTotals()
      })
      .catch(() => {
        // Si falla, el descuento igual se ve al reabrir la factura o al cobrar.
      })
  }, [openDiscounts.length, refreshTotals])

  // Debounced article search
  const handleSearchChange = useCallback(
    debounce((value: string) => {
      setSearchQuery(value)
      setShowResults(value.length >= 2)
    }, 300),
    []
  )

  const handleInputChange = (value: string) => {
    setSearchInput(value)
    handleSearchChange(value)
    if (value) setScanCandidates(null)
  }

  // Debounced client search
  const handleClientSearchChange = useCallback(
    debounce((value: string) => {
      setClientSearchQuery(value)
      setShowClientResults(value.length >= 2)
    }, 300),
    []
  )

  const handleClientInputChange = (value: string) => {
    setClientSearchInput(value)
    handleClientSearchChange(value)
  }

  // Get price based on tier (without IVA - tax is calculated separately)
  const getArticlePrice = (article: Article, tier: 1 | 2 | 3) => {
    switch (tier) {
      case 1:
        return article.sale_price_1
      case 2:
        return article.sale_price_2
      case 3:
        return article.sale_price_3
      default:
        return article.sale_price_1
    }
  }

  // ============ LECTOR DE CÓDIGO DE BARRAS ============
  // Cada escaneo es una unidad. Van en cola, de a uno: dos escaneos seguidos del
  // mismo artículo tienen que sumar 2, y el primero de una factura nueva no
  // puede crear dos facturas.

  const scanFailed = (title: string, description?: string) => {
    playScanErrorSound()
    setLastScan({ ok: false, text: title })
    toast({ title, description, variant: 'destructive' })
  }

  const flashLine = (lineId: number) => {
    setHighlightedLineId(lineId)
    setTimeout(() => setHighlightedLineId((id) => (id === lineId ? null : id)), 1200)
  }

  const addScannedArticle = async (article: Article) => {
    const existing = linesRef.current.find((l) => l.article_id === article.id)
    try {
      if (existing?.id) {
        const updated = await updateLineMutation.mutateAsync({
          lineId: existing.id,
          quantity: existing.quantity + 1,
          unit_price: existing.unit_price,
          discount_percent: existing.discount_percent,
        })
        linesRef.current = linesRef.current.map((l) =>
          l.id === updated.id ? { ...l, quantity: updated.quantity } : l
        )
        flashLine(updated.id)
        setLastScan({
          ok: true,
          text: t('pos.scanAdded', { description: article.description, quantity: updated.quantity }),
        })
      } else {
        const price = getArticlePrice(article, selectedPriceTier)
        setPendingArticle({
          code: article.code,
          description: article.description,
          price,
          stock: article.count_articles,
        })
        const line = await addLineMutation.mutateAsync({
          article_id: article.id,
          quantity: 1,
          unit_price: price,
          discount_percent: 0,
          cabys_code: article.cabys_code,
          ubication_name: article.ubication_name,
        })
        linesRef.current = [...linesRef.current, line]
        if (line.id) flashLine(line.id)
        setLastScan({ ok: true, text: t('pos.scanAdded', { description: article.description, quantity: 1 }) })
      }
      return true
    } catch {
      // La mutación ya mostró el error.
      playScanErrorSound()
      setLastScan({ ok: false, text: t('pos.scanNotAdded', { description: article.description }) })
      return false
    } finally {
      setPendingArticle(null)
    }
  }

  /** Devuelve si el artículo quedó en la factura. */
  const processScanJob = async (job: ScanJob): Promise<boolean> => {
    if ('article' in job) {
      return addScannedArticle(job.article)
    }
    const { code } = job
    if (code.length > 50) {
      scanFailed(t('pos.scanNotFound', { code }))
      return false
    }
    try {
      const { data } = await api.get<{ items: Article[] }>('/articles/lookup', { params: { code } })
      if (data.items.length === 1) {
        setScanCandidates(null)
        return await addScannedArticle(data.items[0])
      }
      playScanErrorSound()
      setShowResults(false)
      setScanCandidates({ code, items: data.items })
      return false
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        scanFailed(t('pos.scanNotFound', { code }))
      } else {
        scanFailed(t('pos.scanError', { code }), t('pos.scanErrorHint'))
      }
      return false
    }
  }

  const processScanJobRef = useRef(processScanJob)
  processScanJobRef.current = processScanJob

  const drainScanQueue = async () => {
    if (scanBusyRef.current) return
    scanBusyRef.current = true
    try {
      while (scanQueueRef.current.length > 0) {
        const job = scanQueueRef.current.shift()!
        let added = false
        try {
          added = await processScanJobRef.current(job)
        } finally {
          job.onDone?.(added)
          setPendingScans((n) => n - 1)
        }
      }
    } finally {
      scanBusyRef.current = false
    }
  }

  const scanBlocked =
    showPaymentModal ||
    finalizeMutation.isPending ||
    saveDraftMutation.isPending ||
    cancelBillMutation.isPending

  const enqueueScan = (job: ScanJob) => {
    primeScanAudio()
    if (scanBlocked) {
      job.onDone?.(false)
      scanFailed(t('pos.scanBlocked'))
      return
    }
    scanQueueRef.current.push(job)
    setPendingScans((n) => n + 1)
    void drainScanQueue()
  }

  useBarcodeScanner((code, target) => {
    // Leído en el buscador: lo que había escrito no queda pegado ni reabre la lista.
    if (target === searchInputRef.current) {
      setSearchInput('')
      handleSearchChange('')
      setShowResults(false)
    }
    enqueueScan({ code: code.trim() })
  }, hasCashboxOpen)

  // La cámara se cierra al agregar; con error queda abierta para leer otro.
  const handleCameraCode = (code: string) =>
    new Promise<boolean>((resolve) => {
      playScanReadSound()
      enqueueScan({ code, onDone: resolve })
    })

  const openCamera = () => {
    primeScanAudio()
    setScanCandidates(null)
    setShowCamera(true)
  }

  useEffect(() => {
    if (!lastScan) return
    const timer = setTimeout(() => setLastScan(null), 10_000)
    return () => clearTimeout(timer)
  }, [lastScan])

  // El botón flotante se esconde mientras se escribe: con el teclado abierto tapa los campos.
  useEffect(() => {
    const onFocusIn = (e: FocusEvent) =>
      setTyping(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)
    const onFocusOut = () => setTyping(false)
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
    }
  }, [])

  // Para elegir entre varios artículos con el mismo código hay que ver la lista.
  useEffect(() => {
    if (scanCandidates) setShowCamera(false)
  }, [scanCandidates])

  // Enter en el buscador: código tecleado a mano (el lector lo captura antes).
  const handleSearchEnter = () => {
    const code = searchInput.trim()
    if (!code) return
    setSearchInput('')
    // Descarta la búsqueda diferida pendiente para que no reabra la lista.
    handleSearchChange('')
    setShowResults(false)
    enqueueScan({ code })
  }

  // Add article to bill
  const handleAddArticle = async (article: Article) => {
    const existing = lines.find((l) => l.article_id === article.id)
    if (existing) {
      toast({
        title: t('pos.articleAlreadyAdded'),
        description: t('pos.articleInBill'),
        variant: 'destructive',
      })
      return
    }

    const price = getArticlePrice(article, selectedPriceTier)

    setSearchInput('')
    setSearchQuery('')
    setShowResults(false)
    setPendingArticle({
      code: article.code,
      description: article.description,
      price: price,
      stock: article.count_articles,
    })

    try {
      await addLineMutation.mutateAsync({
        article_id: article.id,
        quantity: 1,
        unit_price: price,
        discount_percent: 0,
        cabys_code: article.cabys_code,
        ubication_name: article.ubication_name,
      })
    } catch {
      // Error already handled by mutation's onError callback (toast)
    } finally {
      setPendingArticle(null)
    }
  }

  // Select client
  const handleSelectClient = (client: Client) => {
    setSelectedClient(client)
    setClientName(client.name)
    setClientSearchInput(client.name)
    setShowClientResults(false)
    // If the client is configured as exonerated, prefill the exoneration
    // section so the cashier doesn't have to retype it on every bill.
    if (client.is_exonerated) {
      setIsExonerated(true)
      setExonerationType(client.exoneration_type || '')
      setExonerationNumber(client.exoneration_number || '')
      setExonerationInstitution(client.exoneration_institution || '')
      setExonerationInstitutionCode(client.exoneration_institution_code || '')
      setExonerationPercentage(client.exoneration_percentage || 100)
    }
  }

  // Clear client
  const handleClearClient = () => {
    setSelectedClient(null)
    setClientSearchInput('')
    setClientName('')
    setClientNote('')
    setClientIdType('01')
    setClientId('')
    setClientEmail('')
    setReceiverActivityCode('')
    setReceiverActivityName('')
    setTermDays(0)
    setSelectedPriceTier(1)
  }

  // Shared validation before finalizing
  const validateBeforeFinalize = (overridePaymentMethod?: string): boolean => {
    if (lines.length === 0) {
      toast({
        title: t('pos.noItemsError'),
        description: t('pos.addItemsFirst'),
        variant: 'destructive',
      })
      return false
    }

    if (paymentType !== 'credit' && !paymentMethod && !overridePaymentMethod) {
      toast({
        title: t('pos.incompleteData'),
        description: t('pos.selectPaymentMethod'),
        variant: 'destructive',
      })
      return false
    }

    if (!activityCode) {
      toast({
        title: t('pos.incompleteData'),
        description: t('activities.atLeastOneActivity'),
        variant: 'destructive',
      })
      return false
    }

    if (documentType === 'invoice' || paymentType === 'credit') {
      if (!clientName || !clientId) {
        // La cédula va en los datos del receptor, que arrancan contraídos.
        if (!clientId) setShowClientSection(true)
        toast({
          title: t('pos.incompleteData'),
          description: paymentType === 'credit'
            ? t('pos.creditRequiresClient')
            : t('pos.invoiceRequiresClient'),
          variant: 'destructive',
        })
        return false
      }
    }

    const linesMissingCabys = lines.filter(line => !line.cabys_code)
    if (linesMissingCabys.length > 0) {
      toast({
        title: t('pos.cabysRequired'),
        description: t('pos.cabysRequiredDesc'),
        variant: 'destructive',
      })
      return false
    }

    return true
  }

  // Handle payment - finalize bill directly
  const handlePayment = async (overridePaymentMethod?: string) => {
    if (!validateBeforeFinalize(overridePaymentMethod)) return

    // For cash payments, show the payment modal for change calculation
    if (paymentType === 'cash') {
      setShowPaymentModal(true)
      return
    }

    finalizeMutation.mutate({
      payment_type: paymentType,
      amount_paid: totals.total,
    })
  }

  // Quick payment - finalize with specific payment method
  const handleQuickPayment = async (methodCode: string) => {
    setPaymentMethod(methodCode)
    if (!validateBeforeFinalize(methodCode)) return

    finalizeMutation.mutate({
      payment_type: paymentType,
      amount_paid: totals.total,
      payment_method_code: methodCode,
    })
  }

  // Save draft ("continuar luego"). Credit drafts MUST have a linked client,
  // otherwise they get orphaned (client_id null) and never show in the client's
  // account statement / accounts receivable.
  const handleSaveDraft = () => {
    if (paymentType === 'credit' && !clientId) {
      toast({
        title: t('pos.incompleteData'),
        description: t('pos.creditRequiresClient'),
        variant: 'destructive',
      })
      return
    }
    saveDraftMutation.mutate()
  }

  // Handle payment modal confirmation (cash with change calculation)
  const handlePaymentModalConfirm = async (data: PaymentData) => {
    const methodCodeMap: Record<PaymentMethod, string> = {
      [PaymentMethod.CASH]: '01',
      [PaymentMethod.CARD]: '02',
      [PaymentMethod.CHECK]: '03',
      [PaymentMethod.TRANSFER]: '04',
      [PaymentMethod.SINPE]: '06',
      [PaymentMethod.OTHER]: '99',
      [PaymentMethod.CREDIT]: '99',
    }

    finalizeMutation.mutate({
      payment_type: data.paymentMethod === PaymentMethod.CREDIT ? 'credit' : 'cash',
      amount_paid: data.amountReceived,
      payment_method_code: methodCodeMap[data.paymentMethod],
    })
  }

  const isProcessing =
    addLineMutation.isPending ||
    updateLineMutation.isPending ||
    removeLineMutation.isPending ||
    finalizeMutation.isPending ||
    saveDraftMutation.isPending ||
    cancelBillMutation.isPending

  // ============ RENDER ============

  // Show loading while checking cashbox
  if (isLoadingCashbox) {
    return (
      <div className="flex items-center justify-center h-[calc(100dvh-3.5rem)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Show error if no cashbox is open
  if (!hasCashboxOpen) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100dvh-3.5rem)] p-4">
        <div className="text-center max-w-md">
          <Receipt className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">{t('pos.noCashboxOpen')}</h2>
          <p className="text-muted-foreground mb-6">
            {t('pos.mustOpenCashbox')}
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate('/bills')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.back')}
            </Button>
            <Button onClick={() => navigate('/cashbox')}>
              {t('pos.goToCashbox')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-0 pb-12 md:pb-0">
      {/* ========== HEADER ========== */}
      <div className="bg-background md:relative md:bg-transparent">
        <div className="p-4 md:p-0 md:mb-6">
          {/* Mobile Header */}
          <div className="flex items-center gap-3 mb-4 md:hidden">
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 shrink-0"
              onClick={() => navigate('/bills')}
              aria-label={t('common.back')}
            >
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold">{t('pos.title')}</h1>
              <p className="text-xs text-muted-foreground">
                {documentType === 'invoice' ? t('pos.invoiceElectronic') : t('pos.ticketElectronic')}
              </p>
            </div>
            {billId && (
              <span className="text-sm text-muted-foreground">#{billId}</span>
            )}
            {/* En el celular no se ve la columna con Guardar y Cancelar: van en este menú */}
            {billId && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 shrink-0"
                    aria-label={t('pos.moreActions')}
                    disabled={isProcessing}
                  >
                    <MoreVertical className="h-5 w-5" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-48">
                  {documentType !== 'proforma' && (
                    <DropdownMenuItem className="h-11" onSelect={handleSaveDraft}>
                      <Save className="h-4 w-4 mr-2" aria-hidden="true" />
                      {t('pos.saveForLater')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    className="h-11 text-destructive focus:text-destructive"
                    onSelect={() => setConfirmCancelBill(true)}
                  >
                    <X className="h-4 w-4 mr-2" aria-hidden="true" />
                    {documentType === 'proforma' ? t('pos.cancelProforma') : t('pos.cancelBill')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Desktop Header */}
          <div className="hidden md:flex md:items-center md:justify-between md:mb-4">
            <div>
              <h1 className="text-3xl font-bold">{t('bills.newBill')}</h1>
              <p className="text-muted-foreground">
                {documentType === 'invoice' ? t('pos.invoiceElectronic') : t('pos.ticketElectronic')}
              </p>
            </div>
            {billId && (
              <div className="text-right">
                <p className="text-sm text-muted-foreground">{t('bills.billNumber')}</p>
                <p className="text-2xl font-bold text-primary">{billId}</p>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Condiciones de la venta, cliente y búsqueda: a todo el ancho */}
      <div className="px-4 md:px-0">
          {/* Document Type & Payment Config */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger className="h-12">
                <SelectValue placeholder={t('bills.documentType')} />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={paymentType} onValueChange={(v) => setPaymentType(v as 'cash' | 'credit')}>
              <SelectTrigger className="h-12">
                <SelectValue placeholder={t('pos.paymentMethod')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">{t('pos.cash')}</SelectItem>
                <SelectItem value="credit">{t('pos.credit')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Activity Code Selector (required) */}
          {tenantActivities.length > 0 && (
            <div className="mb-4">
              <Label className="text-sm font-medium mb-1 block">{t('activities.economicActivity')} *</Label>
              <Select value={activityCode} onValueChange={setActivityCode}>
                <SelectTrigger className={`h-10 ${!activityCode ? 'border-destructive' : ''}`}>
                  <SelectValue placeholder={t('activities.selectActivity')} />
                </SelectTrigger>
                <SelectContent>
                  {tenantActivities.map((a) => (
                    <SelectItem key={a.code} value={a.code}>
                      {a.code} - {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Client - Single unified input: type a name or search & select */}
          <div className="mb-4 relative" ref={clientSearchRef}>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder={t('pos.clientNamePlaceholder')}
                className={cn(
                  "pl-10 pr-10 h-12 text-base",
                  selectedClient && "border-primary/40 bg-primary/5",
                  documentType === 'invoice' && !clientName && !selectedClient && "border-yellow-500/50"
                )}
                value={clientName}
                onChange={(e) => {
                  if (selectedClient) return // locked when client selected
                  setClientName(e.target.value)
                  handleClientInputChange(e.target.value)
                }}
                onFocus={() => {
                  if (!selectedClient && clientSearchQuery.length >= 2) setShowClientResults(true)
                }}
                readOnly={!!selectedClient}
              />
              {(clientName || selectedClient) && (
                <button
                  type="button"
                  onClick={() => {
                    handleClearClient()
                    setShowClientResults(false)
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-muted rounded-full"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Selected client badge */}
            {selectedClient && (
              <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
                <Badge variant="secondary" className="text-xs font-normal">
                  {selectedClient.identification}
                </Badge>
                {selectedClient.email && <span>{selectedClient.email}</span>}
              </div>
            )}

            {selectedClient && (
              <ClientCreditAlert summary={clientAccount} className="mt-2" />
            )}

            {/* Search results dropdown */}
            {!selectedClient && showClientResults && isSearchingClients && (
              <div className="absolute z-50 w-full mt-1 bg-card border rounded-lg shadow-lg p-4 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                <span className="text-sm text-muted-foreground">{t('pos.searching')}</span>
              </div>
            )}
            {!selectedClient && showClientResults && !isSearchingClients && clientResults && clientResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-card border rounded-lg shadow-lg max-h-48 overflow-auto overscroll-contain">
                {clientResults.map((client) => (
                  <div
                    key={client.id}
                    className="p-3 border-b last:border-b-0 cursor-pointer hover:bg-muted active:bg-muted touch-manipulation"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      handleSelectClient(client)
                      setShowClientResults(false)
                    }}
                  >
                    <p className="font-medium">{client.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {client.identification || '-'} • {client.email || '-'}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Mismo criterio que la validación al cobrar: con los datos del receptor contraídos, avisa qué falta */}
            {documentType === 'invoice' && (!clientName || !clientId) && (
              <p className="text-xs text-yellow-600 mt-1">{t('pos.invoiceRequiresClient')}</p>
            )}
          </div>

          {/* Client free-text note (shown next to the client name everywhere except printed receipts) */}
          <div className="mb-4">
            <Input
              type="text"
              maxLength={60}
              placeholder={t('bills.clientNotePlaceholder')}
              className="h-11 text-base"
              value={clientNote}
              onChange={(e) => setClientNote(e.target.value)}
            />
          </div>

          {/* Datos del receptor: con cliente elegido, en factura o a crédito (ahí la cédula es obligatoria) */}
          {(selectedClient || documentType === 'invoice' || paymentType === 'credit') && (
            <button
              type="button"
              onClick={() => setShowClientSection(!showClientSection)}
              aria-expanded={showClientSection}
              className={cn(
                "w-full flex items-center justify-between p-2.5 rounded-lg border transition-colors mb-4 text-sm",
                showClientSection ? "bg-muted/50 border-muted-foreground/20" : "bg-muted/30"
              )}
            >
              <span className="text-muted-foreground">{t('pos.clientDetails')}</span>
              <span className="flex items-center gap-2 min-w-0">
                {!showClientSection && (clientId || isExonerated) && (
                  <span className="truncate text-xs text-muted-foreground">
                    {[clientId, isExonerated && t('clients.exonerated')].filter(Boolean).join(' · ')}
                  </span>
                )}
                {showClientSection ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
              </span>
            </button>
          )}

          {showClientSection && (selectedClient || documentType === 'invoice' || paymentType === 'credit') && (
            <div className="space-y-4 mb-4 p-4 bg-muted/30 rounded-lg border">
              {/* ID Type + Identification */}
              <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-2 md:gap-3">
                <div>
                  <Label className="text-sm font-medium">{t('pos.idType')}</Label>
                  <Select value={clientIdType} onValueChange={setClientIdType}>
                    <SelectTrigger className="h-12 mt-1">
                      <SelectValue placeholder={t('pos.idType')} />
                    </SelectTrigger>
                    <SelectContent>
                      {ID_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium">{t('pos.identification')} *</Label>
                  <Input
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    placeholder="0-0000-0000"
                    className="h-12 mt-1 text-base"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">{t('pos.email')}</Label>
                <Input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  onBlur={autoSaveClientInfo}
                  placeholder="cliente@email.com"
                  className="h-12 mt-1 text-base"
                />
              </div>

              <div>
                <Label className="text-sm font-medium">{t('activities.receiverActivity')}</Label>
                <div className="mt-1">
                  <ActivityCodeSelect
                    identification={clientId}
                    value={receiverActivityCode}
                    valueName={receiverActivityName}
                    onChange={(code, name) => {
                      setReceiverActivityCode(code)
                      setReceiverActivityName(name)
                    }}
                    clientActivityCode={selectedClient?.activity_code}
                    clientActivityName={selectedClient?.activity_code_name}
                  />
                </div>
              </div>

              {clientInfoSaved && (
                <p className="text-xs text-green-500">{t('activities.clientInfoSaved')}</p>
              )}

              {paymentType === 'credit' && (
                <div>
                  <Label className="text-sm font-medium">{t('pos.creditDays')}</Label>
                  <Input
                    type="number"
                    value={termDays || ''}
                    onChange={(e) => setTermDays(parseInt(e.target.value) || 0)}
                    placeholder={creditDaysPlaceholder}
                    className="h-12 mt-1 text-base"
                  />
                </div>
              )}

              {/* Exoneration Section (v4.4) */}
              <div className="pt-4 border-t">
                <label className="flex items-center gap-3 cursor-pointer touch-manipulation">
                  <input
                    type="checkbox"
                    checked={isExonerated}
                    onChange={(e) => setIsExonerated(e.target.checked)}
                    className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm font-medium">
                    {t('pos.exonerated')}
                  </span>
                </label>

                {isExonerated && (
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="text-sm font-medium">{t('pos.exonerationType')}</Label>
                      <Select value={exonerationType} onValueChange={setExonerationType}>
                        <SelectTrigger className="h-12 mt-1">
                          <SelectValue placeholder={t('common.select')} />
                        </SelectTrigger>
                        <SelectContent>
                          {EXONERATION_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-2 md:gap-3">
                      <div>
                        <Label className="text-sm font-medium">{t('pos.exonerationNumber')}</Label>
                        <Input
                          value={exonerationNumber}
                          onChange={(e) => setExonerationNumber(e.target.value)}
                          placeholder="AL-000000"
                          className="h-12 mt-1 text-base"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">{t('pos.exonerationPercentage')}</Label>
                        <Input
                          type="number"
                          min="1"
                          max="100"
                          value={exonerationPercentage}
                          onChange={(e) => setExonerationPercentage(parseInt(e.target.value) || 100)}
                          className="h-12 mt-1 text-base"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">{t('pos.exonerationInstitution')}</Label>
                      <Select
                        value={exonerationInstitutionCode}
                        onValueChange={(value) => {
                          setExonerationInstitutionCode(value)
                          // Mirror human label into the legacy free-text field
                          // unless the user picks "99=Otros" (then they type it).
                          if (value !== '99') {
                            const opt = EXONERATION_INSTITUTION_OPTIONS.find((o) => o.value === value)
                            setExonerationInstitution(opt ? opt.label : '')
                          }
                        }}
                      >
                        <SelectTrigger className="h-12 mt-1">
                          <SelectValue placeholder={t('common.select')} />
                        </SelectTrigger>
                        <SelectContent>
                          {EXONERATION_INSTITUTION_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.value} — {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {exonerationInstitutionCode === '99' && (
                      <div>
                        <Label className="text-sm font-medium">
                          {t('clients.exonerationInstitutionOther')}
                        </Label>
                        <Input
                          value={exonerationInstitution}
                          onChange={(e) => setExonerationInstitution(e.target.value)}
                          placeholder={t('pos.exonerationInstitution')}
                          className="h-12 mt-1 text-base"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Article Search */}
          <div className="relative" ref={searchContainerRef}>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  ref={searchInputRef}
                  placeholder={t('pos.searchArticle')}
                  aria-label={t('pos.searchArticle')}
                  autoComplete="off"
                  spellCheck={false}
                  enterKeyHint="search"
                  className="pl-10 pr-10 h-12 text-base"
                  value={searchInput}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onFocus={() => {
                    if (searchQuery.length >= 2) setShowResults(true)
                    // En táctiles el teclado tapa la mitad de abajo: el buscador sube para que se vean los resultados.
                    if (cameraAvailable) {
                      setTimeout(() => searchContainerRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 300)
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleSearchEnter()
                    } else if (e.key === 'Escape') {
                      setScanCandidates(null)
                    }
                  }}
                />
                {pendingScans > 0 && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 animate-spin text-primary" />
                )}
              </div>
              <Select
                value={selectedPriceTier.toString()}
                onValueChange={(v) => setSelectedPriceTier(parseInt(v) as 1 | 2 | 3)}
              >
                <SelectTrigger className="w-20 h-12" aria-label={t('pos.priceTier')}>
                  <SelectValue placeholder={t('pos.priceTier')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">P1</SelectItem>
                  <SelectItem value="2" disabled={isSeller}>P2</SelectItem>
                  <SelectItem value="3" disabled={isSeller}>P3</SelectItem>
                </SelectContent>
              </Select>
              {/* En tablet, botón con texto junto al buscador; en el celular es el botón flotante */}
              {cameraAvailable && (
                <Button
                  className="hidden md:inline-flex h-12 shrink-0"
                  onClick={openCamera}
                  title={t('pos.cameraScan')}
                >
                  <ScanBarcode className="h-5 w-5 mr-2" aria-hidden="true" />
                  {t('pos.scanButton')}
                </Button>
              )}
            </div>

            {openDiscounts.length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm space-y-1" role="status">
                <p className="font-medium">{t('approvals.openDiscounts')}</p>
                {openDiscounts.map((request) => (
                  <button
                    key={request.id}
                    type="button"
                    className="block underline underline-offset-2 text-left"
                    onClick={() => request.line_id && leavePosTo(`/bills/${billId}/edit-line/${request.line_id}`)}
                  >
                    {t('approvals.openDiscountLine', {
                      description: request.article_description ?? '',
                      percent: request.discount_percent ?? 0,
                    })}
                  </button>
                ))}
              </div>
            )}

            {/* Varios artículos comparten el código escaneado */}
            {scanCandidates && (
              <div className="absolute z-50 w-full mt-1 bg-card border border-primary rounded-lg shadow-lg max-h-[60dvh] md:max-h-80 overflow-auto">
                <div className="flex items-center justify-between gap-2 px-4 py-2 border-b bg-primary/5">
                  <p className="text-sm font-medium">
                    {t('pos.scanCandidates', { code: scanCandidates.code })}
                  </p>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 shrink-0"
                    onClick={() => setScanCandidates(null)}
                    aria-label={t('common.close')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {scanCandidates.items.map((article) => (
                  <div
                    key={article.id}
                    className="flex items-center justify-between p-4 border-b last:border-b-0 active:bg-muted hover:bg-muted/50 cursor-pointer touch-manipulation"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      setScanCandidates(null)
                      enqueueScan({ article })
                      searchInputRef.current?.focus()
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{article.code}</p>
                      <p className="text-sm text-muted-foreground truncate">{article.description}</p>
                      {article.ubication_name && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 truncate">
                          {article.ubication_name}
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="font-bold text-primary">
                        {formatCurrency(getArticlePrice(article, selectedPriceTier))}
                      </p>
                      <p
                        className={cn(
                          'text-xs',
                          article.count_articles <= 0 ? 'text-destructive font-medium' : 'text-muted-foreground'
                        )}
                      >
                        {t('pos.stock')}: {article.count_articles}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Article Search Results */}
            {showResults && isSearching && (
              <div className="absolute z-50 w-full mt-1 bg-card border rounded-lg shadow-lg p-4 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">{t('pos.searching')}</span>
              </div>
            )}
            {showResults && !isSearching && searchResults && searchResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-card border rounded-lg shadow-lg max-h-64 overflow-auto">
                {searchResults.map((article) => (
                  <div
                    key={article.id}
                    className="flex items-center justify-between p-4 border-b last:border-b-0 active:bg-muted cursor-pointer touch-manipulation"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      handleAddArticle(article)
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{article.code}</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {article.description}
                      </p>
                      {article.alternative_code && (
                        <p className="text-xs text-muted-foreground/70 truncate">
                          {t('articles.alternativeCode')}: {article.alternative_code}
                        </p>
                      )}
                      {article.ubication_name && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 truncate">
                          {article.ubication_name}
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="font-bold text-primary">
                        {formatCurrency(getArticlePrice(article, selectedPriceTier))}
                      </p>
                      <p
                        className={cn(
                          'text-xs',
                          article.count_articles <= 0
                            ? 'text-destructive font-medium'
                            : article.count_articles <= 5
                            ? 'text-yellow-600 dark:text-yellow-500'
                            : 'text-muted-foreground'
                        )}
                      >
                        {t('pos.stock')}: {article.count_articles}
                        {article.count_articles <= 0 && ' ⚠️'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {showResults && !isSearching && searchResults?.length === 0 && searchQuery.length >= 2 && (
              <div className="absolute z-50 w-full mt-1 bg-card border rounded-lg shadow-lg p-4 text-center text-muted-foreground">
                {t('pos.noArticlesFound')}
              </div>
            )}
          </div>

          {lastScan && (
            <p
              role="status"
              className={cn(
                'mt-2 flex items-center gap-1.5 text-sm truncate',
                lastScan.ok ? 'text-success' : 'text-destructive'
              )}
            >
              {lastScan.ok ? (
                <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
              ) : (
                <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
              )}
              <span className="truncate">{lastScan.text}</span>
            </p>
          )}
      </div>

      {/* Desde tablet: líneas a la izquierda; totales y cobro al lado, fijos al bajar por las líneas */}
      <div className="md:grid md:grid-cols-[minmax(0,1fr)_17rem] lg:grid-cols-3 md:gap-6 md:items-start md:mt-6">
          {/* Items List */}
          <div className="p-4 md:p-0 lg:col-span-2">
          {lines.length === 0 && !pendingArticle ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ShoppingCart className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <p className="text-lg font-medium text-muted-foreground">{t('pos.noItems')}</p>
              <p className="text-sm text-muted-foreground">
                {t('pos.addItemsToStart')}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Pending Article */}
              {pendingArticle && (
                <div className="flex items-center gap-3 p-3 bg-card border border-primary/50 rounded-lg animate-pulse">
                  <Loader2 className="h-5 w-5 animate-spin text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{pendingArticle.code}</p>
                    <p className="text-sm text-muted-foreground truncate">
                      {pendingArticle.description}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary">{formatCurrency(pendingArticle.price)}</p>
                    <p
                      className={cn(
                        'text-xs',
                        pendingArticle.stock <= 0 ? 'text-destructive' : 'text-muted-foreground'
                      )}
                    >
                      {pendingArticle.stock <= 0 ? `⚠️ ${t('pos.noStock')}` : t('pos.addingItem')}
                    </p>
                  </div>
                </div>
              )}

              {/* Line Items */}
              {lines.map((line) => (
                <div
                  key={line.id || line.article_id}
                  className={cn(
                    'p-3 bg-card border rounded-lg transition-colors',
                    line.id === highlightedLineId && 'border-primary bg-primary/10'
                  )}
                >
                  {/* Mobile Layout */}
                  <div className="lg:hidden space-y-2">
                    {/* Row 1: Code + Total + Delete */}
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium truncate flex-1">{line.article_code}</p>
                      <p className="font-bold text-primary shrink-0">{formatCurrency(line.total)}</p>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 text-destructive shrink-0 -mr-2"
                        onClick={() => line.id && setLineToRemove(line)}
                        disabled={isProcessing}
                        aria-label={t('pos.removeLine')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Row 2: Description + Location + Stock badge (fila compartida) */}
                    <p className="text-sm text-muted-foreground line-clamp-1">
                      {line.article_description}
                    </p>
                    {(line.ubication_name || (typeof line.article_stock === 'number' && documentType !== 'proforma')) && (
                      <div className="flex items-center gap-2 flex-wrap">
                        {line.ubication_name && (
                          <p className="text-xs text-blue-600 dark:text-blue-400 truncate">
                            {line.ubication_name}
                          </p>
                        )}
                        {typeof line.article_stock === 'number' && documentType !== 'proforma' && (() => {
                          // Threshold de bajo stock: minimo configurado del articulo
                          // si esta seteado (>0); sino fallback a 5.
                          const lowThreshold = line.article_minimum_stock && line.article_minimum_stock > 0
                            ? line.article_minimum_stock
                            : 5
                          const isOut = line.article_stock <= 0
                          const isLow = !isOut && line.article_stock <= lowThreshold
                          return (
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-[10px] leading-none px-1.5 py-0.5 h-5 shrink-0',
                                isOut
                                  ? 'text-destructive border-destructive bg-red-50 dark:bg-red-950 dark:border-red-700'
                                  : isLow
                                  ? 'text-yellow-600 border-yellow-400 bg-yellow-50 dark:bg-yellow-950 dark:text-yellow-500 dark:border-yellow-700'
                                  : 'text-green-700 border-green-300 bg-green-50 dark:bg-green-950 dark:text-green-400 dark:border-green-800'
                              )}
                            >
                              {(isOut || isLow) && <AlertTriangle className="h-2.5 w-2.5 mr-1" />}
                              {t('pos.stock')}: {line.article_stock}
                            </Badge>
                          )
                        })()}
                      </div>
                    )}

                    {/* Row 3: CABYS warning */}
                    {!line.cabys_code && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-yellow-600 border-yellow-400 bg-yellow-50 dark:bg-yellow-950 dark:text-yellow-500 dark:border-yellow-700 text-xs">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          CABYS
                        </Badge>
                        <Button
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-xs text-blue-600"
                          onClick={() => leavePosTo(`/articles/${line.article_id}/cabys?return=/bills/new?continue=${billId}`)}
                        >
                          {t('pos.addCabys')}
                        </Button>
                      </div>
                    )}

                    {/* Row 4: Price + Quantity Controls + Edit */}
                    <div className="flex items-center justify-between gap-2 pt-1 border-t">
                      <p className="text-sm text-muted-foreground">
                        {formatCurrency(line.unit_price)} × {line.quantity}
                        {line.discount_percent > 0 && (
                          <span className="text-green-600 ml-1">-{line.discount_percent}%</span>
                        )}
                      </p>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-10 w-10"
                          aria-label={t('pos.decreaseQuantity')}
                          disabled={isProcessing || line.quantity <= 1}
                          onClick={() => {
                            if (line.id && line.quantity > 1) {
                              updateLineMutation.mutate({
                                lineId: line.id,
                                quantity: line.quantity - 1,
                                unit_price: line.unit_price,
                                discount_percent: line.discount_percent,
                              })
                            }
                          }}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <Input
                          type="number"
                          min="1"
                          defaultValue={line.quantity}
                          key={`mobile-qty-${line.id}-${line.quantity}`}
                          className="w-14 h-9 text-center font-bold px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          disabled={isProcessing}
                          onBlur={(e) => {
                            const newQty = parseInt(e.target.value) || 1
                            if (line.id && newQty !== line.quantity && newQty >= 1) {
                              updateLineMutation.mutate({
                                lineId: line.id,
                                quantity: newQty,
                                unit_price: line.unit_price,
                                discount_percent: line.discount_percent,
                              })
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              (e.target as HTMLInputElement).blur()
                            }
                          }}
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-10 w-10"
                          aria-label={t('pos.increaseQuantity')}
                          disabled={isProcessing}
                          onClick={() => {
                            if (line.id) {
                              updateLineMutation.mutate({
                                lineId: line.id,
                                quantity: line.quantity + 1,
                                unit_price: line.unit_price,
                                discount_percent: line.discount_percent,
                              })
                            }
                          }}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-10 w-10 ml-1"
                          aria-label={t('pos.editLine')}
                          onClick={() => line.id && leavePosTo(`/bills/${billId}/edit-line/${line.id}`)}
                          disabled={isProcessing}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden lg:flex lg:items-start lg:gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{line.article_code}</p>
                      {/* Dos renglones: en tablet horizontal la columna es angosta */}
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {line.article_description}
                      </p>
                      {(line.ubication_name || (typeof line.article_stock === 'number' && documentType !== 'proforma')) && (
                        <div className="flex items-center gap-2 flex-wrap">
                          {line.ubication_name && (
                            <p className="text-xs text-blue-600 dark:text-blue-400 truncate">
                              {line.ubication_name}
                            </p>
                          )}
                          {typeof line.article_stock === 'number' && documentType !== 'proforma' && (() => {
                            const lowThreshold = line.article_minimum_stock && line.article_minimum_stock > 0
                              ? line.article_minimum_stock
                              : 5
                            const isOut = line.article_stock <= 0
                            const isLow = !isOut && line.article_stock <= lowThreshold
                            return (
                              <Badge
                                variant="outline"
                                className={cn(
                                  'text-[10px] leading-none px-1.5 py-0.5 h-5 shrink-0',
                                  isOut
                                    ? 'text-destructive border-destructive bg-red-50 dark:bg-red-950 dark:border-red-700'
                                    : isLow
                                    ? 'text-yellow-600 border-yellow-400 bg-yellow-50 dark:bg-yellow-950 dark:text-yellow-500 dark:border-yellow-700'
                                    : 'text-green-700 border-green-300 bg-green-50 dark:bg-green-950 dark:text-green-400 dark:border-green-800'
                                )}
                              >
                                {(isOut || isLow) && <AlertTriangle className="h-2.5 w-2.5 mr-1" />}
                                {t('pos.stock')}: {line.article_stock}
                              </Badge>
                            )
                          })()}
                        </div>
                      )}
                      {!line.cabys_code && (
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-yellow-600 border-yellow-400 bg-yellow-50 dark:bg-yellow-950 dark:text-yellow-500 dark:border-yellow-700">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {t('pos.cabysWarning')}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs text-blue-600"
                            onClick={() => leavePosTo(`/articles/${line.article_id}/cabys?return=/bills/new?continue=${billId}`)}
                          >
                            {t('pos.addCabys')}
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        aria-label={t('pos.decreaseQuantity')}
                        disabled={isProcessing || line.quantity <= 1}
                        onClick={() => {
                          if (line.id && line.quantity > 1) {
                            updateLineMutation.mutate({
                              lineId: line.id,
                              quantity: line.quantity - 1,
                              unit_price: line.unit_price,
                              discount_percent: line.discount_percent,
                            })
                          }
                        }}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <Input
                        type="number"
                        min="1"
                        defaultValue={line.quantity}
                        key={`desktop-qty-${line.id}-${line.quantity}`}
                        className="w-16 h-8 text-center font-bold px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        disabled={isProcessing}
                        onBlur={(e) => {
                          const newQty = parseInt(e.target.value) || 1
                          if (line.id && newQty !== line.quantity && newQty >= 1) {
                            updateLineMutation.mutate({
                              lineId: line.id,
                              quantity: newQty,
                              unit_price: line.unit_price,
                              discount_percent: line.discount_percent,
                            })
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            (e.target as HTMLInputElement).blur()
                          }
                        }}
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        aria-label={t('pos.increaseQuantity')}
                        disabled={isProcessing}
                        onClick={() => {
                          if (line.id) {
                            updateLineMutation.mutate({
                              lineId: line.id,
                              quantity: line.quantity + 1,
                              unit_price: line.unit_price,
                              discount_percent: line.discount_percent,
                            })
                          }
                        }}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Total & Actions */}
                    <div className="flex items-center gap-1">
                      <div className="text-right min-w-[70px]">
                        <p className="font-bold">{formatCurrency(line.total)}</p>
                        {line.discount_percent > 0 && (
                          <p className="text-xs text-green-600">-{line.discount_percent}%</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        aria-label={t('pos.editLine')}
                        onClick={() => line.id && leavePosTo(`/bills/${billId}/edit-line/${line.id}`)}
                        disabled={isProcessing}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 text-destructive shrink-0"
                        onClick={() => line.id && setLineToRemove(line)}
                        disabled={isProcessing}
                        aria-label={t('pos.removeLine')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>

        {/* Desktop Sidebar */}
        <div className="hidden md:block space-y-4 md:sticky md:top-0">
          {/* Totals Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('receipt.subtotal')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('receipt.subtotal')}</span>
                <span>{formatCurrency(totals.subtotal)}</span>
              </div>
              {totals.total_line_discount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>{t('receipt.lineDiscount')}</span>
                  <span>-{formatCurrency(totals.total_line_discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('receipt.iva')} (13%)</span>
                <span>{formatCurrency(totals.total_sale_tax)}</span>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between font-bold text-xl">
                  <span>{t('receipt.total')}</span>
                  <span className="text-primary">{formatCurrency(totals.total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Method (oculto para proformas — no son pagables) */}
          {documentType === 'proforma' ? (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t('proformas.proformaInfo')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-sm font-medium mb-1 block">
                    {t('proformas.expirationDate')}
                  </Label>
                  <Input
                    type="date"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                    className="h-12"
                  />
                </div>

                <Button
                  className="w-full h-12"
                  onClick={handleSaveDraft}
                  disabled={lines.length === 0 || !billId || isProcessing}
                >
                  {saveDraftMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {t('proformas.saveProforma')}
                </Button>

                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => setConfirmCancelBill(true)}
                  disabled={!billId || isProcessing}
                >
                  {cancelBillMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <X className="h-4 w-4 mr-2" />
                  )}
                  {t('pos.cancel')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  {paymentType === 'credit' ? t('pos.creditDays') : t('pos.paymentMethod')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {paymentType !== 'credit' ? (
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('pos.selectPaymentMethod')} />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS_HACIENDA.map((method) => (
                        <SelectItem key={method.value} value={method.value}>
                          {method.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type="number"
                    min="0"
                    value={termDays || ''}
                    onChange={(e) => setTermDays(parseInt(e.target.value) || 0)}
                    placeholder={creditDaysPlaceholder}
                    className="h-12"
                  />
                )}

                <Button
                  className="w-full h-12"
                  onClick={() => handlePayment()}
                  disabled={lines.length === 0 || isProcessing}
                >
                  {finalizeMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Receipt className="h-4 w-4 mr-2" />
                  )}
                  {t('pos.finalizeSale')}
                </Button>

                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    onClick={handleSaveDraft}
                    disabled={!billId || isProcessing}
                  >
                    {saveDraftMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    {t('pos.saveForLater')}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setConfirmCancelBill(true)}
                    disabled={!billId || isProcessing}
                  >
                    {cancelBillMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <X className="h-4 w-4 mr-2" />
                    )}
                    {t('pos.cancel')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ========== MOBILE FOOTER ========== */}
      {/* En el POS no va el menú inferior: esta barra ocupa su lugar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-card border-t px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] space-y-2 md:hidden shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
        {/* Total Display */}
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            {lines.length} {lines.length === 1 ? t('pos.item') : t('pos.items')}
          </p>
          <p className="text-2xl font-bold tabular-nums">{formatCurrency(totals.total)}</p>
        </div>

        {/* Payment Buttons (proforma usa un único botón Guardar) */}
        {documentType === 'proforma' ? (
          <Button
            className="w-full h-12"
            onClick={() => saveDraftMutation.mutate()}
            disabled={lines.length === 0 || !billId || isProcessing}
          >
            {saveDraftMutation.isPending ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <Save className="h-5 w-5 mr-2" />
            )}
            {t('proformas.saveProforma')}
          </Button>
        ) : (
          // A crédito no se elige medio de pago: un solo botón. La condición se cambia arriba.
          paymentType === 'credit' ? (
            <Button
              className="w-full h-12"
              onClick={() => {
                setPaymentMethod('99')
                handlePayment('99')
              }}
              disabled={lines.length === 0 || isProcessing}
            >
              {finalizeMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : (
                <FileText className="h-5 w-5 mr-2" />
              )}
              {t('pos.finalizeSale')} · {t('pos.credit')}
            </Button>
          ) : (
          <div className="grid grid-cols-4 gap-2">
            <Button
              className={cn('h-12 flex-col gap-0.5', lines.length === 0 && 'opacity-50')}
              onClick={() => { setPaymentMethod('01'); if (validateBeforeFinalize('01')) setShowPaymentModal(true) }} // Efectivo
              disabled={lines.length === 0 || isProcessing}
            >
              {finalizeMutation.isPending && paymentMethod === '01' ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Banknote className="h-5 w-5" />
              )}
              <span className="text-xs">{t('pos.paymentCash')}</span>
            </Button>
            <Button
              variant="outline"
              className={cn('h-12 flex-col gap-0.5', lines.length === 0 && 'opacity-50')}
              onClick={() => handleQuickPayment('02')} // Tarjeta
              disabled={lines.length === 0 || isProcessing}
            >
              {finalizeMutation.isPending && paymentMethod === '02' ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <CreditCard className="h-5 w-5" />
              )}
              <span className="text-xs">{t('pos.paymentCard')}</span>
            </Button>
            <Button
              variant="outline"
              className={cn('h-12 flex-col gap-0.5', lines.length === 0 && 'opacity-50')}
              onClick={() => handleQuickPayment('06')} // SINPE Móvil
              disabled={lines.length === 0 || isProcessing}
            >
              {finalizeMutation.isPending && paymentMethod === '06' ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Smartphone className="h-5 w-5" />
              )}
              <span className="text-xs">{t('pos.paymentSinpe')}</span>
            </Button>
            {/* Cheque y transferencia: abre la ventana de pago con todos los medios */}
            <Button
              variant="outline"
              className={cn('h-12 flex-col gap-0.5', lines.length === 0 && 'opacity-50')}
              onClick={() => {
                setPaymentMethod('04')
                if (validateBeforeFinalize('04')) setShowPaymentModal(true)
              }}
              disabled={lines.length === 0 || isProcessing}
            >
              <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
              <span className="text-xs">{t('pos.paymentOther')}</span>
            </Button>
          </div>
          )
        )}
      </div>

      <AlertDialog open={creditBlockedNotice} onOpenChange={setCreditBlockedNotice}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" aria-hidden="true" />
              {t('pos.creditBlockedTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('pos.creditBlockedDescription', { name: selectedClient?.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              className="h-12 text-destructive hover:text-destructive sm:h-10"
              onClick={() => {
                setCreditBlockedNotice(false)
                if (billId) setConfirmCancelBill(true)
                else navigate('/bills')
              }}
            >
              {documentType === 'proforma' ? t('pos.cancelProforma') : t('pos.cancelBill')}
            </Button>
            <AlertDialogAction autoFocus className="h-12 text-base font-semibold sm:h-10 sm:px-6">
              {t('pos.keepEditing')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmCancelBill} onOpenChange={setConfirmCancelBill}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {documentType === 'proforma'
                ? t('pos.cancelProformaTitle', { id: billId ?? '' })
                : t('pos.cancelBillTitle', { id: billId ?? '' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {documentType === 'proforma' ? t('pos.cancelProformaDescription') : t('pos.cancelBillDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('pos.keepBill')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => cancelBillMutation.mutate()}
            >
              {documentType === 'proforma' ? t('pos.cancelProforma') : t('pos.cancelBill')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!lineToRemove} onOpenChange={(open) => !open && setLineToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('pos.removeLineTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {lineToRemove?.article_code} — {lineToRemove?.article_description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (lineToRemove?.id) removeLineMutation.mutate(lineToRemove.id)
                setLineToRemove(null)
              }}
            >
              {t('pos.removeLine')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Celular: escanear queda al alcance del pulgar aunque el buscador haya quedado arriba */}
      {cameraAvailable && !typing && !showCamera && (
        <Button
          className="md:hidden fixed right-4 z-40 h-14 w-14 rounded-full shadow-lg bottom-[calc(8rem+env(safe-area-inset-bottom))]"
          onClick={openCamera}
          aria-label={t('pos.cameraScan')}
        >
          <ScanBarcode className="h-6 w-6" aria-hidden="true" />
        </Button>
      )}

      <CameraScannerDialog
        open={showCamera}
        onOpenChange={setShowCamera}
        onCode={handleCameraCode}
      />

      {/* Payment Modal for cash - change calculation */}
      <PaymentModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        total={totals.total}
        clientName={clientName || selectedClient?.name}
        clientHasCredit={selectedClient ? selectedClient.credit_limit > 0 && !selectedClient.credit_blocked : false}
        availableCredit={selectedClient ? Math.max(0, selectedClient.credit_limit - selectedClient.current_balance) : 0}
        creditDays={termDays || selectedClient?.payment_days || 30}
        onConfirmPayment={handlePaymentModalConfirm}
        isProcessing={finalizeMutation.isPending}
        initialPaymentMethod={getPaymentMethodFromCode(paymentMethod || '01')}
      />
    </div>
  )
}
